import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Bold,
  FileText,
  FolderOpen,
  Highlighter,
  List,
  Paperclip,
  Upload,
  X,
  Plus,
  Sliders,
  Trash2,
} from "lucide-react";

import styles from "./StaticPages.module.css";
import ToggleSwitch from "../../../components/BaseComponents/Toggle/ToggleSwitch";
import BaseModal from "../../../components/BaseComponents/BaseModal/BaseModal";
import { useToast } from "../../../context/ToastContext";
import {
  useGetStaticPages,
  useUpdateStaticPages,
  useUploadStaticPageDocument,
} from "../../../api/cms/useStaticPagesApi";
import "../../../styles/cms-form-controls.css";

/* ── helpers ─────────────────────────────────────────────────────────────── */

const slugify = (value = "") =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const toText = (value) => (value == null ? "" : String(value));

const toSortOrder = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

/** Pseudo group key used to reach pages whose groupKey matches no group. */
const UNGROUPED_KEY = "__ungrouped__";

// Client-only handle so a page stays selectable before it has a server id.
let uidSeq = 0;
const nextUid = () => {
  uidSeq += 1;
  return `sp-${uidSeq}`;
};

/** Readable file size. Bytes are never what an author wants to read. */
function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "PDF";
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return Math.round(n / 1024) + " KB";
  return (n / (1024 * 1024)).toFixed(1).replace(/\.0$/, "") + " MB";
}

function normalizeSection(section) {
  return {
    // 'faq' renders its items as an accordion on the site; 'text' is prose.
    type: section?.type === "faq" ? "faq" : "text",
    heading: toText(section?.heading),
    body: toText(section?.body),
    bullets: Array.isArray(section?.bullets) ? section.bullets.map(toText) : [],
    items: Array.isArray(section?.items)
      ? section.items.map((it) => ({ q: toText(it?.q), a: toText(it?.a) }))
      : [],
    // A PDF attached to this section: its address plus enough to label it.
    documentUrl: toText(section?.documentUrl),
    documentName: toText(section?.documentName),
    documentSize: Number.isFinite(Number(section?.documentSize))
      ? Number(section.documentSize)
      : null,
    // The wording the visitor reads, e.g. "Download als PDF bestand".
    documentLabel: toText(section?.documentLabel),
  };
}

function normalizeGroup(group, index) {
  const title = toText(group?.title);
  return {
    groupKey:
      slugify(group?.groupKey || title || `group-${index + 1}`) ||
      `group-${index + 1}`,
    title,
    subtitle: toText(group?.subtitle),
    sortOrder: toSortOrder(group?.sortOrder ?? index),
    active: group?.active !== false,
  };
}

function normalizePage(page, index) {
  return {
    _uid: nextUid(),
    id: page?.id ?? null,
    groupKey: toText(page?.groupKey),
    slug: toText(page?.slug),
    title: toText(page?.title),
    subtitle: toText(page?.subtitle),
    intro: toText(page?.intro),
    sections: Array.isArray(page?.sections)
      ? page.sections.map(normalizeSection)
      : [],
    seoTitle: toText(page?.seoTitle),
    seoDescription: toText(page?.seoDescription),
    sortOrder: toSortOrder(page?.sortOrder ?? index),
    active: page?.active !== false,
  };
}

function normalizeData(data) {
  return {
    groups: (Array.isArray(data?.groups) ? data.groups : []).map(normalizeGroup),
    pages: (Array.isArray(data?.pages) ? data.pages : []).map(normalizePage),
  };
}

/**
 * Strip client-only fields and hand the API exactly the contract shape.
 * The API replaces the whole collection, so this always serialises EVERY
 * group and page in the draft — never just the group currently on screen.
 */
function buildPayload(draft) {
  return {
    groups: (draft.groups || []).map((group) => ({
      groupKey: group.groupKey,
      title: toText(group.title).trim(),
      subtitle: toText(group.subtitle).trim(),
      sortOrder: toSortOrder(group.sortOrder),
      active: group.active !== false,
    })),
    pages: (draft.pages || []).map((page) => ({
      ...(page.id ? { id: page.id } : {}),
      groupKey: page.groupKey,
      slug: slugify(page.slug),
      title: toText(page.title).trim(),
      subtitle: toText(page.subtitle).trim(),
      intro: toText(page.intro),
      sections: (page.sections || []).map((section) => ({
        type: section.type === "faq" ? "faq" : "text",
        heading: toText(section.heading).trim(),
        body: toText(section.body),
        bullets: (section.bullets || [])
          .map((bullet) => toText(bullet).trim())
          .filter(Boolean),
        // Q&A pairs for an accordion section. A question is required; an entry
        // with only an answer has nothing to click, so it is dropped.
        items: (section.items || [])
          .map((it) => ({ q: toText(it.q).trim(), a: toText(it.a) }))
          .filter((it) => it.q),
        // Both this and normalizeSection rebuild a section from a fixed key set,
        // so a field missing from either is dropped without any error.
        documentUrl: toText(section.documentUrl).trim(),
        documentName: toText(section.documentName).trim(),
        documentSize: Number.isFinite(Number(section.documentSize))
          ? Number(section.documentSize)
          : null,
        documentLabel: toText(section.documentLabel).trim(),
      })),
      seoTitle: toText(page.seoTitle).trim(),
      seoDescription: toText(page.seoDescription).trim(),
      sortOrder: toSortOrder(page.sortOrder),
      active: page.active !== false,
    })),
  };
}

const EMPTY_DRAFT = { groups: [], pages: [] };

const moveInList = (list, index, direction) => {
  const next = [...list];
  const target = index + direction;
  if (target < 0 || target >= next.length) return list;
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
};

const MoveControl = ({ onMoveUp, onMoveDown, disableUp, disableDown }) => (
  <div className={styles.moveControl}>
    <button
      type="button"
      className={styles.moveBtn}
      onClick={onMoveUp}
      disabled={disableUp}
      title="Move up"
    >
      <ChevronUp size={13} />
    </button>
    <button
      type="button"
      className={styles.moveBtn}
      onClick={onMoveDown}
      disabled={disableDown}
      title="Move down"
    >
      <ChevronDown size={13} />
    </button>
  </div>
);

/* ── screen ──────────────────────────────────────────────────────────────── */

const StaticPages = () => {
  const { showToast } = useToast();
  const getApi = useGetStaticPages();
  const updateApi = useUpdateStaticPages();

  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [savedSnapshot, setSavedSnapshot] = useState(
    JSON.stringify(buildPayload(EMPTY_DRAFT))
  );
  const [loaded, setLoaded] = useState(false);
  const [selectedPageUid, setSelectedPageUid] = useState(null);
  const [groupSettingsOpen, setGroupSettingsOpen] = useState(false);
  const [confirmState, setConfirmState] = useState(null);

  // The sidebar links to ?page=<slug> — one entry per page. ?group=<key> is
  // still honoured so older links and the overview cards keep working.
  const [searchParams, setSearchParams] = useSearchParams();
  const activePageSlug = searchParams.get("page") || null;

  // The group a ?page= link opened, resolved ONCE and then held.
  //
  // This used to be re-derived on every render by looking the URL's slug up in
  // the draft. The slug is an editable field on the page being edited, so the
  // first keystroke in it stopped the lookup matching, the group key fell to
  // null, and null renders the overview: the editor threw the author out to
  // About SUNSKY mid-edit and lost what they were typing. Editing the title of
  // a page with no slug yet did it too, because the title fills an empty slug.
  //
  // Nothing that survives an edit may key off a value the edit can change, so
  // the URL is read once to find the page and the selection is held by uid
  // after that.
  const [resolvedGroupKey, setResolvedGroupKey] = useState(null);
  const resolvedParamRef = useRef(null);
  const activeGroupKey = searchParams.get("group") || resolvedGroupKey;

  // Lets a navigation carry a page selection across the URL change.
  const pendingPageUidRef = useRef(null);
  useEffect(() => {
    setSelectedPageUid(pendingPageUidRef.current);
    pendingPageUidRef.current = null;
  }, [activeGroupKey]);

  // Opening ?page=<slug> selects that page and its group. Guarded on the URL
  // value rather than the draft, so it resolves when the link changes or the
  // data first loads, and never again while the author is typing. The uid is
  // parked in pendingPageUidRef because setting the group re-runs the effect
  // above, which would otherwise clear the selection this just made.
  useEffect(() => {
    if (!activePageSlug) {
      // No ?page= any more, so nothing is remembered: without this the group
      // stayed resolved and "back to overview" landed on the editor again.
      resolvedParamRef.current = null;
      setResolvedGroupKey(null);
      return;
    }
    if (resolvedParamRef.current === activePageSlug) return;
    const match = draft.pages.find((p) => p.slug === activePageSlug);
    if (!match) return; // data not loaded yet; this re-runs when it is
    resolvedParamRef.current = activePageSlug;
    pendingPageUidRef.current = match._uid;
    setResolvedGroupKey(match.groupKey ?? null);
    setSelectedPageUid(match._uid);
  }, [activePageSlug, draft.pages]);

  const goToGroup = useCallback(
    (groupKey, pageUid = null) => {
      if ((groupKey || null) === activeGroupKey) {
        setSelectedPageUid(pageUid);
        return;
      }
      pendingPageUidRef.current = pageUid;
      setSearchParams(groupKey ? { group: groupKey } : {});
    },
    [activeGroupKey, setSearchParams]
  );

  const getExecute = getApi.execute;
  const updateExecute = updateApi.execute;
  // Keeps the selected page across a save (uids are regenerated on reload).
  const reselectSlugRef = useRef(null);

  const applyData = useCallback((data) => {
    const normalized = normalizeData(data);
    setDraft(normalized);
    setSavedSnapshot(JSON.stringify(buildPayload(normalized)));
    const wantedSlug = reselectSlugRef.current;
    reselectSlugRef.current = null;
    setSelectedPageUid(
      wantedSlug
        ? normalized.pages.find((p) => p.slug === wantedSlug)?._uid ?? null
        : null
    );
    return normalized;
  }, []);

  const load = useCallback(async () => {
    try {
      const data = await getExecute({ all: true });
      applyData(data);
    } catch {
      /* error surfaces through getApi.error */
    } finally {
      setLoaded(true);
    }
  }, [getExecute, applyData]);

  useEffect(() => {
    load();
  }, [load]);

  const isDirty = useMemo(
    () => JSON.stringify(buildPayload(draft)) !== savedSnapshot,
    [draft, savedSnapshot]
  );

  /* ── derived ─────────────────────────────────────────────────────────── */

  const pagesByGroup = useMemo(() => {
    const map = new Map();
    draft.groups.forEach((group) => map.set(group.groupKey, []));
    const orphans = [];
    draft.pages.forEach((page) => {
      const bucket = map.get(page.groupKey);
      if (bucket) bucket.push(page);
      else orphans.push(page);
    });
    return { map, orphans };
  }, [draft]);

  const duplicateSlugs = useMemo(() => {
    const seen = new Map();
    draft.pages.forEach((page) => {
      const slug = slugify(page.slug);
      if (!slug) return;
      seen.set(slug, (seen.get(slug) || 0) + 1);
    });
    return new Set(
      [...seen.entries()].filter(([, count]) => count > 1).map(([slug]) => slug)
    );
  }, [draft.pages]);

  const missingSlugCount = useMemo(
    () => draft.pages.filter((page) => !slugify(page.slug)).length,
    [draft.pages]
  );

  const isUngroupedView = activeGroupKey === UNGROUPED_KEY;
  const activeGroup = isUngroupedView
    ? null
    : draft.groups.find((group) => group.groupKey === activeGroupKey) || null;
  const groupPages = isUngroupedView
    ? pagesByGroup.orphans
    : pagesByGroup.map.get(activeGroupKey) || [];
  // A ?group= key that resolves to neither a group nor any page is a dead link.
  const groupNotFound =
    Boolean(activeGroupKey) && !activeGroup && groupPages.length === 0;

  const selectedPage =
    draft.pages.find((page) => page._uid === selectedPageUid) || null;

  /* ── group mutations ─────────────────────────────────────────────────── */

  const updateGroup = (groupKey, updates) => {
    setDraft((prev) => ({
      ...prev,
      groups: prev.groups.map((group) =>
        group.groupKey === groupKey ? { ...group, ...updates } : group
      ),
    }));
  };

  const renameGroupKey = (oldKey, rawKey) => {
    const nextKey = slugify(rawKey);
    if (!nextKey || nextKey === oldKey) return;
    if (draft.groups.some((group) => group.groupKey === nextKey)) {
      showToast("Another group already uses that key", "error");
      return;
    }
    setDraft((prev) => ({
      groups: prev.groups.map((group) =>
        group.groupKey === oldKey ? { ...group, groupKey: nextKey } : group
      ),
      // Pages point at their group by key — carry them across the rename.
      pages: prev.pages.map((page) =>
        page.groupKey === oldKey ? { ...page, groupKey: nextKey } : page
      ),
    }));
    if (activeGroupKey === oldKey) goToGroup(nextKey, selectedPageUid);
  };

  const addGroup = () => {
    const usedKeys = new Set(draft.groups.map((group) => group.groupKey));
    let index = draft.groups.length + 1;
    let groupKey = `group-${index}`;
    while (usedKeys.has(groupKey)) {
      index += 1;
      groupKey = `group-${index}`;
    }
    const group = {
      groupKey,
      title: `New Group ${index}`,
      subtitle: "",
      sortOrder: draft.groups.length,
      active: true,
    };
    setDraft((prev) => ({ ...prev, groups: [...prev.groups, group] }));
    setGroupSettingsOpen(true);
    goToGroup(groupKey);
  };

  const removeGroup = (groupKey) => {
    setDraft((prev) => ({
      groups: prev.groups.filter((group) => group.groupKey !== groupKey),
      pages: prev.pages.filter((page) => page.groupKey !== groupKey),
    }));
    goToGroup(null);
  };

  const moveGroup = (index, direction) => {
    setDraft((prev) => ({
      ...prev,
      groups: moveInList(prev.groups, index, direction).map((group, i) => ({
        ...group,
        sortOrder: i,
      })),
    }));
  };

  /* ── page mutations ──────────────────────────────────────────────────── */

  const updatePage = (uid, updates) => {
    setDraft((prev) => ({
      ...prev,
      pages: prev.pages.map((page) =>
        page._uid === uid ? { ...page, ...updates } : page
      ),
    }));
  };

  // Slug follows the title only while the author hasn't set one yet.
  const updatePageTitle = (page, title) => {
    const updates = { title };
    if (!page.slug.trim()) updates.slug = slugify(title);
    updatePage(page._uid, updates);
  };

  const addPage = (groupKey) => {
    // After the last page in the group. Counting the pages instead collided
    // with the last page whenever the numbering started at 1, as it does in the
    // live data: the new page tied with the one it was meant to follow, and the
    // dashboard and the website broke that tie in different orders.
    const lastSortOrder = Math.max(
      -1,
      ...(pagesByGroup.map.get(groupKey) || []).map((p) => toSortOrder(p.sortOrder))
    );
    const page = normalizePage(
      {
        groupKey,
        title: "New Page",
        slug: "",
        sortOrder: lastSortOrder + 1,
        active: true,
        sections: [{ heading: "", body: "", bullets: [], documentUrl: "", documentName: "", documentSize: null, documentLabel: "" }],
      },
      0
    );
    setDraft((prev) => ({ ...prev, pages: [...prev.pages, page] }));
    goToGroup(groupKey, page._uid);
  };

  const removePage = (uid) => {
    setDraft((prev) => ({
      ...prev,
      pages: prev.pages.filter((page) => page._uid !== uid),
    }));
    setSelectedPageUid((prev) => (prev === uid ? null : prev));
  };

  const movePage = (groupKey, index, direction) => {
    setDraft((prev) => {
      const inGroup = prev.pages.filter((page) => page.groupKey === groupKey);
      const reordered = moveInList(inGroup, index, direction);
      if (reordered === inGroup) return prev;
      // The list is drawn in array order, so the pages have to swap places in
      // the array, not just take new numbers. Renumbering alone left the list
      // standing still until the next save, and a second click on the same row
      // did nothing. The group's pages go back into the slots it already holds,
      // so pages of other groups stay where they are.
      let slot = 0;
      return {
        ...prev,
        pages: prev.pages.map((page) => {
          if (page.groupKey !== groupKey) return page;
          const moved = { ...reordered[slot], sortOrder: slot };
          slot += 1;
          return moved;
        }),
      };
    });
  };

  /* ── section mutations ───────────────────────────────────────────────── */

  const updateSections = (uid, updater) => {
    setDraft((prev) => ({
      ...prev,
      pages: prev.pages.map((page) =>
        page._uid === uid
          ? { ...page, sections: updater(page.sections || []) }
          : page
      ),
    }));
  };

  const addSection = (uid) =>
    updateSections(uid, (sections) => [
      ...sections,
      { heading: "", body: "", bullets: [], documentUrl: "", documentName: "", documentSize: null, documentLabel: "" },
    ]);

  const updateSection = (uid, index, updates) =>
    updateSections(uid, (sections) =>
      sections.map((section, i) =>
        i === index ? { ...section, ...updates } : section
      )
    );

  /* ── Body formatting ──────────────────────────────────────────────────────
     The site renders three markers in a section body: **bold**, ==highlight==,
     and a line starting with "- " as a bullet. Authors should not have to
     remember that, so these buttons write the markers around whatever is
     selected and then put the caret back where it belongs. */
  const bodyRefs = useRef({});

  const applyMarker = (uid, index, kind) => {
    const key = uid + "-" + index;
    const el = bodyRefs.current[key];
    if (!el) return;

    const { selectionStart: from, selectionEnd: to, value } = el;
    const selected = value.slice(from, to);
    let next;
    let caretFrom;
    let caretTo;

    if (kind === "bullet") {
      // Whole lines, so selecting a paragraph bullets every line of it.
      const lineStart = value.lastIndexOf("\n", Math.max(0, from - 1)) + 1;
      const nextBreak = value.indexOf("\n", to);
      const lineEnd = nextBreak === -1 ? value.length : nextBreak;
      const lines = value.slice(lineStart, lineEnd).split("\n");
      // Pressing it again on an already-bulleted block takes the bullets off.
      const allBulleted = lines.every((l) => !l.trim() || /^\s*-\s+/.test(l));
      const rewritten = lines
        .map((l) => {
          if (allBulleted) return l.replace(/^(\s*)-\s+/, "$1");
          return l.trim() ? "- " + l.trim() : l;
        })
        .join("\n");
      next = value.slice(0, lineStart) + rewritten + value.slice(lineEnd);
      caretFrom = lineStart;
      caretTo = lineStart + rewritten.length;
    } else {
      const wrap = kind === "bold" ? "**" : "==";
      const placeholder = kind === "bold" ? "bold text" : "highlighted";
      const already =
        selected.startsWith(wrap) &&
        selected.endsWith(wrap) &&
        selected.length > wrap.length * 2;
      const inner = already ? selected.slice(wrap.length, -wrap.length) : selected;
      const text = inner || placeholder;
      const replacement = already ? inner : wrap + text + wrap;
      next = value.slice(0, from) + replacement + value.slice(to);
      caretFrom = from + (already ? 0 : wrap.length);
      caretTo = caretFrom + (already ? inner.length : text.length);
    }

    updateSection(uid, index, { body: next });
    // Once React has re-rendered with the new value, restore the selection so
    // the author keeps typing instead of hunting for the caret.
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caretFrom, caretTo);
    });
  };

  /* ── A PDF attached to a section ────────────────────────────────────────── */
  const { uploadDocument } = useUploadStaticPageDocument();
  const [uploadingKey, setUploadingKey] = useState(null);
  const docInputs = useRef({});

  const handleSectionDocument = async (uid, index, file) => {
    if (!file) return;
    const key = uid + "-" + index;

    if (!/\.pdf$/i.test(file.name) || file.type !== "application/pdf") {
      showToast("Choose a PDF file", "error");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      showToast("That PDF is over 15 MB. Please use a smaller file.", "error");
      return;
    }

    setUploadingKey(key);
    try {
      const result = await uploadDocument(file);
      if (!result?.documentUrl) throw new Error("No document URL returned");
      updateSection(uid, index, {
        documentUrl: result.documentUrl,
        documentName: result.fileName || file.name,
        documentSize: result.sizeBytes ?? file.size ?? null,
      });
      showToast("PDF attached. Save the page to publish it.", "success");
    } catch (err) {
      showToast(
        err?.response?.data?.message || err?.message || "Could not upload the PDF",
        "error"
      );
    } finally {
      setUploadingKey(null);
      // Cleared so choosing the SAME file again still fires onChange.
      if (docInputs.current[key]) docInputs.current[key].value = "";
    }
  };

  const removeSectionDocument = (uid, index) =>
    updateSection(uid, index, {
      documentUrl: "",
      documentName: "",
      documentSize: null,
    });

  const removeSection = (uid, index) =>
    updateSections(uid, (sections) => sections.filter((_, i) => i !== index));

  const moveSection = (uid, index, direction) =>
    updateSections(uid, (sections) => moveInList(sections, index, direction));

  const updateBullets = (uid, sectionIndex, updater) =>
    updateSections(uid, (sections) =>
      sections.map((section, i) =>
        i === sectionIndex
          ? { ...section, bullets: updater(section.bullets || []) }
          : section
      )
    );

  const updateItems = (uid, sectionIndex, updater) =>
    updateSections(uid, (sections) =>
      sections.map((section, i) =>
        i === sectionIndex
          ? { ...section, items: updater(section.items || []) }
          : section
      )
    );

  const setSectionType = (uid, sectionIndex, type) =>
    updateSections(uid, (sections) =>
      sections.map((section, i) => (i === sectionIndex ? { ...section, type } : section))
    );

  /* ── actions ─────────────────────────────────────────────────────────── */

  const handleSave = async () => {
    if (duplicateSlugs.size > 0) {
      showToast("Fix the duplicate slugs before saving", "error");
      return;
    }
    if (missingSlugCount > 0) {
      showToast("Every page needs a slug before saving", "error");
      return;
    }
    try {
      reselectSlugRef.current = selectedPage ? slugify(selectedPage.slug) : null;
      // buildPayload always sends the complete collection — a group-scoped
      // screen must never PUT a subset or the API would delete the rest.
      const data = await updateExecute(buildPayload(draft));
      applyData(data);
      showToast("Static pages saved successfully", "success");
    } catch (err) {
      reselectSlugRef.current = null;
      showToast(
        err.response?.data?.message || "Failed to save static pages",
        "error"
      );
    }
  };

  const handleCancel = () => {
    reselectSlugRef.current = selectedPage ? slugify(selectedPage.slug) : null;
    load();
  };

  const confirmTitle =
    confirmState?.type === "group" ? "Remove group" : "Remove page";

  const handleConfirm = () => {
    if (!confirmState) return;
    if (confirmState.type === "group") removeGroup(confirmState.groupKey);
    else removePage(confirmState.uid);
    setConfirmState(null);
  };

  /* ── shared fragments ────────────────────────────────────────────────── */

  const actionBar = (
    <div className={styles.actionWrapper}>
      <div className={styles.actionBar}>
        <div className={styles.actionStatus}>
          {updateApi.loading
            ? "Saving…"
            : isDirty
              ? "Unsaved changes"
              : "All changes saved"}
        </div>
        <div className={styles.actionButtons}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={handleCancel}
            disabled={updateApi.loading || getApi.loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={updateApi.loading}
          >
            {updateApi.loading ? "Saving…" : "Save Static Pages"}
          </button>
        </div>
      </div>
    </div>
  );

  const confirmModal = (
    <BaseModal
      open={Boolean(confirmState)}
      onClose={() => setConfirmState(null)}
      title={confirmTitle}
      width="440px"
      footer={
        <div className={styles.modalActions}>
          <button
            type="button"
            className={styles.smallBtn}
            onClick={() => setConfirmState(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.dangerConfirmBtn}
            onClick={handleConfirm}
          >
            Remove
          </button>
        </div>
      }
    >
      <p className={styles.modalText}>
        {confirmState?.type === "group"
          ? `Remove the group "${confirmState?.label}"${
              confirmState?.count ? ` and its ${confirmState.count} page(s)` : ""
            }? This is applied when you save.`
          : `Remove "${confirmState?.label}"? This is applied when you save.`}
      </p>
    </BaseModal>
  );

  const duplicateBanner = duplicateSlugs.size > 0 && (
    <div className={styles.warnBox}>
      <AlertTriangle size={15} />
      <span>
        Duplicate slug{duplicateSlugs.size > 1 ? "s" : ""}:{" "}
        {[...duplicateSlugs].join(", ")}. Slugs must be unique across all static
        pages.
      </span>
    </div>
  );

  /* ── loading ─────────────────────────────────────────────────────────── */

  if (!loaded) {
    return (
      <div className={`${styles.page} cms-form-scope`}>
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}>§</div>
            <div>
              <h1>CMS - Static Pages</h1>
              <p>Loading…</p>
            </div>
          </div>
        </div>
        <p className={styles.loadingText}>Loading static pages…</p>
      </div>
    );
  }

  /* ── MODE B: overview / index ────────────────────────────────────────── */

  if (!activeGroupKey) {
    return (
      <div className={`${styles.page} cms-form-scope`}>
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}>§</div>
            <div>
              <h1>CMS - Static Pages</h1>
              <p>
                Legal and informational pages, grouped into the sub-menus shown
                on the website
              </p>
            </div>
          </div>
          <div className={styles.headerMeta}>
            <span>{draft.groups.length} groups</span>
            <span>{draft.pages.length} pages</span>
            <button type="button" className={styles.primarySmallBtn} onClick={addGroup}>
              <Plus size={14} /> Add group
            </button>
          </div>
        </div>

        {getApi.error && <div className={styles.errorBox}>{getApi.error}</div>}
        {duplicateBanner}

        {draft.groups.length === 0 ? (
          <div className={styles.card}>
            <div className={styles.blankState}>
              <FolderOpen size={26} />
              <h3>No page groups yet</h3>
              <p>
                Groups become the Static Pages sub-menu on the website. Add your
                first one to start writing pages.
              </p>
              <button
                type="button"
                className={styles.primarySmallBtn}
                onClick={addGroup}
              >
                <Plus size={14} /> Add group
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.groupGrid}>
            {draft.groups.map((group, groupIndex) => {
              const count = (pagesByGroup.map.get(group.groupKey) || []).length;
              return (
                <div key={group.groupKey} className={styles.groupCard}>
                  <button
                    type="button"
                    className={styles.groupCardMain}
                    onClick={() => goToGroup(group.groupKey)}
                  >
                    <span className={styles.groupCardIcon}>
                      <FolderOpen size={18} />
                    </span>
                    <span className={styles.groupCardTitle}>
                      {group.title || group.groupKey}
                      {!group.active && (
                        <span className={styles.offBadge}>Off</span>
                      )}
                    </span>
                    <span className={styles.groupCardSub}>
                      {group.subtitle || "No subtitle"}
                    </span>
                    <span className={styles.groupCardFoot}>
                      <span className={styles.countBadge}>
                        {count} page{count === 1 ? "" : "s"}
                      </span>
                      <code className={styles.keyChip}>{group.groupKey}</code>
                      <ChevronRight size={15} className={styles.groupCardChev} />
                    </span>
                  </button>
                  <div className={styles.groupCardMove}>
                    <MoveControl
                      onMoveUp={() => moveGroup(groupIndex, -1)}
                      onMoveDown={() => moveGroup(groupIndex, 1)}
                      disableUp={groupIndex === 0}
                      disableDown={groupIndex === draft.groups.length - 1}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {pagesByGroup.orphans.length > 0 && (
          <div className={styles.card}>
            <div className={styles.sectionHeader}>
              <h3>
                <AlertTriangle size={16} /> Pages without a group
              </h3>
              <button
                type="button"
                className={styles.smallBtn}
                onClick={() => goToGroup(UNGROUPED_KEY)}
              >
                Review {pagesByGroup.orphans.length}
              </button>
            </div>
            <p className={styles.hint}>
              These pages point at a group key that no longer exists. Open one to
              move it into a group.
            </p>
            <div className={styles.orphanList}>
              {pagesByGroup.orphans.map((page) => (
                <button
                  key={page._uid}
                  type="button"
                  className={styles.orphanRow}
                  onClick={() => goToGroup(UNGROUPED_KEY, page._uid)}
                >
                  <FileText size={14} />
                  <span className={styles.orphanTitle}>
                    {page.title || "Untitled page"}
                  </span>
                  <code className={styles.keyChip}>
                    {page.groupKey || "(no group key)"}
                  </code>
                  <ChevronRight size={14} />
                </button>
              ))}
            </div>
          </div>
        )}

        {actionBar}
        {confirmModal}
      </div>
    );
  }

  /* ── MODE A: a single group ──────────────────────────────────────────── */

  const backLink = (
    <Link to="/cms/static-pages" className={styles.backLink}>
      <ChevronLeft size={13} /> Static Pages
    </Link>
  );

  if (groupNotFound) {
    return (
      <div className={`${styles.page} cms-form-scope`}>
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}>§</div>
            <div>
              {backLink}
              <h1>Group not found</h1>
              <p>Nothing here uses the key “{activeGroupKey}”</p>
            </div>
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.blankState}>
            <FolderOpen size={26} />
            <h3>This group no longer exists</h3>
            <p>
              It may have been renamed or removed. Pick a group from the Static
              Pages overview.
            </p>
            <Link to="/cms/static-pages" className={styles.primaryLink}>
              Back to Static Pages
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const groupHeading = activeGroup
    ? activeGroup.title || activeGroup.groupKey
    : "Ungrouped pages";
  const groupSubtitle = activeGroup
    ? activeGroup.subtitle ||
      "Pages in this group appear under it in the website menu"
    : "These pages point at a group key that no longer exists — move each one into a group";

  return (
    <div className={`${styles.page} cms-form-scope`}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>§</div>
          <div>
            {backLink}
            <h1>{groupHeading}</h1>
            <p>{groupSubtitle}</p>
          </div>
        </div>
        <div className={styles.headerMeta}>
          <span>
            {groupPages.length} page{groupPages.length === 1 ? "" : "s"}
          </span>
          {activeGroup && !activeGroup.active && (
            <span className={styles.offBadge}>Off</span>
          )}
        </div>
      </div>

      {getApi.error && <div className={styles.errorBox}>{getApi.error}</div>}
      {duplicateBanner}

      {activeGroup && (
        <div
          className={`${styles.card} ${styles.groupSettingsCard} ${
            groupSettingsOpen ? styles.cardOpen : styles.cardCollapsed
          }`}
        >
          <div className={styles.sectionHeader}>
            <button
              type="button"
              className={styles.cardTitleBtn}
              onClick={() => setGroupSettingsOpen((prev) => !prev)}
              aria-expanded={groupSettingsOpen}
            >
              <ChevronRight size={15} className={styles.cardTitleChev} />
              <h3>
                <Sliders size={16} /> Group settings
              </h3>
              {!groupSettingsOpen && (
                <span className={styles.cardTitleMeta}>
                  <code className={styles.keyChip}>{activeGroup.groupKey}</code>
                  <span>sort {activeGroup.sortOrder}</span>
                </span>
              )}
            </button>
            <div className={styles.inlineActions}>
              <div className={styles.toggleField}>
                <span>Active</span>
                <ToggleSwitch
                  checked={activeGroup.active}
                  onChange={() =>
                    updateGroup(activeGroup.groupKey, {
                      active: !activeGroup.active,
                    })
                  }
                />
              </div>
            </div>
          </div>

          <div className={styles.grid2}>
            <div className={styles.field}>
              <label>Group title</label>
              <input
                value={activeGroup.title}
                onChange={(e) =>
                  updateGroup(activeGroup.groupKey, { title: e.target.value })
                }
                placeholder="e.g. Legal"
              />
            </div>
            <div className={styles.field}>
              <label>Group key</label>
              <input
                defaultValue={activeGroup.groupKey}
                key={activeGroup.groupKey}
                onBlur={(e) =>
                  renameGroupKey(activeGroup.groupKey, e.target.value)
                }
                placeholder="e.g. legal"
              />
              <p className={styles.hint}>
                Used internally to link pages to this group. Renaming it moves
                every page in the group.
              </p>
            </div>
          </div>

          <div className={styles.grid2}>
            <div className={styles.field}>
              <label>Group subtitle</label>
              <input
                value={activeGroup.subtitle}
                onChange={(e) =>
                  updateGroup(activeGroup.groupKey, { subtitle: e.target.value })
                }
                placeholder="Short line shown under the group heading"
              />
            </div>
            <div className={styles.field}>
              <label>Sort order</label>
              <input
                type="number"
                value={activeGroup.sortOrder}
                onChange={(e) =>
                  updateGroup(activeGroup.groupKey, {
                    sortOrder: toSortOrder(e.target.value),
                  })
                }
              />
            </div>
          </div>

          <button
            type="button"
            className={styles.dangerBtn}
            onClick={() =>
              setConfirmState({
                type: "group",
                groupKey: activeGroup.groupKey,
                label: activeGroup.title || activeGroup.groupKey,
                count: groupPages.length,
              })
            }
          >
            <Trash2 size={14} /> Remove group
          </button>
        </div>
      )}

      <div className={styles.workspace}>
        {/* ── LEFT: pages in THIS group ───────────────────────────────── */}
        <aside className={styles.pagesPanel}>
          <div className={styles.panelHeader}>
            <h3>Pages</h3>
            <span className={styles.countBadge}>{groupPages.length}</span>
          </div>

          <div className={styles.pageList}>
            {groupPages.length === 0 && (
              <p className={styles.emptyState}>
                No pages in this group yet.
              </p>
            )}
            {groupPages.map((page, index) => {
              const slug = slugify(page.slug);
              const duplicate = slug && duplicateSlugs.has(slug);
              return (
                <div key={page._uid} className={styles.pageRow}>
                  <button
                    type="button"
                    className={`${styles.pageBtn} ${
                      selectedPageUid === page._uid ? styles.pageBtnActive : ""
                    }`}
                    onClick={() => setSelectedPageUid(page._uid)}
                  >
                    <span className={styles.pageIndex}>{index + 1}</span>
                    <span className={styles.pageInfo}>
                      <span className={styles.pageTitle}>
                        {page.title || "Untitled page"}
                      </span>
                      <span className={styles.pageSlug}>
                        /{slug || "no-slug"}
                      </span>
                    </span>
                    {duplicate && (
                      <AlertTriangle
                        size={14}
                        className={styles.warnIcon}
                        aria-label="Duplicate slug"
                      />
                    )}
                    {!page.active && (
                      <span className={styles.offBadge}>Off</span>
                    )}
                  </button>
                  {!isUngroupedView && (
                    <MoveControl
                      onMoveUp={() => movePage(activeGroupKey, index, -1)}
                      onMoveDown={() => movePage(activeGroupKey, index, 1)}
                      disableUp={index === 0}
                      disableDown={index === groupPages.length - 1}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {!isUngroupedView && (
            <button
              type="button"
              className={styles.addPageBtn}
              onClick={() => addPage(activeGroupKey)}
            >
              <Plus size={14} /> Add page
            </button>
          )}
        </aside>

        {/* ── RIGHT: page editor ──────────────────────────────────────── */}
        <div className={styles.editor}>
          {!selectedPage && (
            <div className={styles.card}>
              <div className={styles.blankState}>
                <FileText size={26} />
                <h3>
                  {groupPages.length === 0
                    ? "This group has no pages yet"
                    : "No page selected"}
                </h3>
                <p>
                  {groupPages.length === 0
                    ? "Add a page to start writing its content."
                    : "Pick a page on the left to edit its content."}
                </p>
                {!isUngroupedView && (
                  <button
                    type="button"
                    className={styles.primarySmallBtn}
                    onClick={() => addPage(activeGroupKey)}
                  >
                    <Plus size={14} /> Add page
                  </button>
                )}
              </div>
            </div>
          )}

          {selectedPage && (
            <>
              <div className={styles.card}>
                <div className={styles.sectionHeader}>
                  <h3>
                    <FileText size={16} /> Page details
                  </h3>
                  <div className={styles.inlineActions}>
                    <div className={styles.toggleField}>
                      <span>Active</span>
                      <ToggleSwitch
                        checked={selectedPage.active}
                        onChange={() =>
                          updatePage(selectedPage._uid, {
                            active: !selectedPage.active,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className={styles.grid2}>
                  <div className={styles.field}>
                    <label>Title</label>
                    <input
                      value={selectedPage.title}
                      onChange={(e) =>
                        updatePageTitle(selectedPage, e.target.value)
                      }
                      placeholder="e.g. Terms & Conditions"
                    />
                  </div>
                  <div className={styles.field}>
                    <label>Slug</label>
                    <input
                      value={selectedPage.slug}
                      onChange={(e) =>
                        updatePage(selectedPage._uid, { slug: e.target.value })
                      }
                      onBlur={(e) =>
                        updatePage(selectedPage._uid, {
                          slug: slugify(e.target.value),
                        })
                      }
                      placeholder="terms-and-conditions"
                    />
                    {!slugify(selectedPage.slug) ? (
                      <p className={styles.inlineWarn}>
                        <AlertTriangle size={12} /> A slug is required — the page
                        URL is built from it.
                      </p>
                    ) : duplicateSlugs.has(slugify(selectedPage.slug)) ? (
                      <p className={styles.inlineWarn}>
                        <AlertTriangle size={12} /> Another page already uses this
                        slug. Slugs must be unique.
                      </p>
                    ) : (
                      <p className={styles.hint}>
                        Auto-filled from the title until you edit it.
                      </p>
                    )}
                  </div>
                </div>

                <div className={styles.field}>
                  <label>Subtitle</label>
                  <input
                    value={selectedPage.subtitle}
                    onChange={(e) =>
                      updatePage(selectedPage._uid, { subtitle: e.target.value })
                    }
                    placeholder="Short line shown under the page title"
                  />
                </div>

                <div className={styles.field}>
                  <label>Intro</label>
                  <textarea
                    rows={4}
                    value={selectedPage.intro}
                    onChange={(e) =>
                      updatePage(selectedPage._uid, { intro: e.target.value })
                    }
                    placeholder="Opening paragraph shown above the sections"
                  />
                  <p className={styles.hint}>
                    Plain text. Leave a blank line between paragraphs.
                  </p>
                </div>

                <div className={styles.grid2}>
                  <div className={styles.field}>
                    <label>Group</label>
                    <select
                      value={selectedPage.groupKey}
                      onChange={(e) => {
                        const groupKey = e.target.value;
                        updatePage(selectedPage._uid, { groupKey });
                        goToGroup(groupKey, selectedPage._uid);
                      }}
                    >
                      {!draft.groups.some(
                        (group) => group.groupKey === selectedPage.groupKey
                      ) && (
                        <option value={selectedPage.groupKey}>
                          {selectedPage.groupKey || "(no group)"}
                        </option>
                      )}
                      {draft.groups.map((group) => (
                        <option key={group.groupKey} value={group.groupKey}>
                          {group.title || group.groupKey}
                        </option>
                      ))}
                    </select>
                    <p className={styles.hint}>
                      Moving a page opens the group it lands in.
                    </p>
                  </div>
                  <div className={styles.field}>
                    <label>Sort order</label>
                    <input
                      type="number"
                      value={selectedPage.sortOrder}
                      onChange={(e) =>
                        updatePage(selectedPage._uid, {
                          sortOrder: toSortOrder(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>

                <div className={styles.field}>
                  <label>SEO title</label>
                  <input
                    value={selectedPage.seoTitle}
                    onChange={(e) =>
                      updatePage(selectedPage._uid, { seoTitle: e.target.value })
                    }
                    placeholder="Browser tab / search result title"
                  />
                </div>

                <div className={styles.field}>
                  <label>SEO description</label>
                  <textarea
                    rows={3}
                    value={selectedPage.seoDescription}
                    onChange={(e) =>
                      updatePage(selectedPage._uid, {
                        seoDescription: e.target.value,
                      })
                    }
                    placeholder="Summary shown in search results"
                  />
                </div>

                <button
                  type="button"
                  className={styles.dangerBtn}
                  onClick={() =>
                    setConfirmState({
                      type: "page",
                      uid: selectedPage._uid,
                      label: selectedPage.title || "this page",
                    })
                  }
                >
                  <Trash2 size={14} /> Remove page
                </button>
              </div>

              <div className={styles.card}>
                <div className={styles.sectionHeader}>
                  <h3>Sections</h3>
                  <button
                    type="button"
                    className={styles.smallBtn}
                    onClick={() => addSection(selectedPage._uid)}
                  >
                    <Plus size={14} /> Add Section
                  </button>
                </div>

                {(selectedPage.sections || []).length === 0 && (
                  <p className={styles.emptyState}>
                    No sections yet. Add one to start writing the page body.
                  </p>
                )}

                {(selectedPage.sections || []).map((section, index) => (
                  <div key={index} className={styles.itemCard}>
                    <div className={styles.sectionHeader}>
                      <span className={styles.itemIndex}>
                        Section {index + 1}
                      </span>
                      {/* Prose or a Q&A accordion. Switching to Q&A does not
                          discard the body text, so it can be switched back. */}
                      <div className={styles.typeSwitch} role="group" aria-label="Section type">
                        <button
                          type="button"
                          className={`${styles.typeBtn} ${section.type !== "faq" ? styles.typeBtnOn : ""}`}
                          onClick={() => setSectionType(selectedPage._uid, index, "text")}
                        >
                          Text
                        </button>
                        <button
                          type="button"
                          className={`${styles.typeBtn} ${section.type === "faq" ? styles.typeBtnOn : ""}`}
                          onClick={() => setSectionType(selectedPage._uid, index, "faq")}
                        >
                          Q&amp;A
                        </button>
                      </div>
                      <div className={styles.inlineActions}>
                        <MoveControl
                          onMoveUp={() =>
                            moveSection(selectedPage._uid, index, -1)
                          }
                          onMoveDown={() =>
                            moveSection(selectedPage._uid, index, 1)
                          }
                          disableUp={index === 0}
                          disableDown={index === selectedPage.sections.length - 1}
                        />
                        <button
                          type="button"
                          className={styles.iconDangerBtn}
                          onClick={() => removeSection(selectedPage._uid, index)}
                          title="Remove section"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    <div className={styles.field}>
                      <label>Heading</label>
                      <input
                        value={section.heading}
                        onChange={(e) =>
                          updateSection(selectedPage._uid, index, {
                            heading: e.target.value,
                          })
                        }
                        placeholder="Optional section heading"
                      />
                    </div>

                    <div className={styles.field}>
                      <label>Body</label>

                      {/* Writes the markers the site renders, so nobody has to
                          remember the syntax. */}
                      <div className={styles.formatBar}>
                        <button
                          type="button"
                          className={styles.formatBtn}
                          title="Bold (wraps the selected text)"
                          onClick={() =>
                            applyMarker(selectedPage._uid, index, "bold")
                          }
                        >
                          <Bold size={15} />
                          Bold
                        </button>
                        <button
                          type="button"
                          className={styles.formatBtn}
                          title="Highlight (wraps the selected text)"
                          onClick={() =>
                            applyMarker(selectedPage._uid, index, "highlight")
                          }
                        >
                          <Highlighter size={15} />
                          Highlight
                        </button>
                        <button
                          type="button"
                          className={styles.formatBtn}
                          title="Bullet list (turns the selected lines into bullets)"
                          onClick={() =>
                            applyMarker(selectedPage._uid, index, "bullet")
                          }
                        >
                          <List size={15} />
                          Bullets
                        </button>
                      </div>

                      <textarea
                        ref={(el) => {
                          bodyRefs.current[selectedPage._uid + "-" + index] = el;
                        }}
                        className={styles.bodyArea}
                        rows={12}
                        value={section.body}
                        onChange={(e) =>
                          updateSection(selectedPage._uid, index, {
                            body: e.target.value,
                          })
                        }
                        placeholder="Section text…"
                      />
                      <p className={styles.hint}>
                        Leave a blank line between paragraphs. Use the buttons
                        above, or type <code>**bold**</code>,{" "}
                        <code>==highlight==</code>, and <code>- </code> at the
                        start of a line for a bullet.
                      </p>
                    </div>

                    {/* An attached PDF, shown on the site as a document card the
                        visitor can read in place or open in a new tab. */}
                    <div className={styles.field}>
                      <label>Attached PDF</label>
                      {section.documentUrl ? (
                        <div className={styles.docCard}>
                          <span className={styles.docIcon}>
                            <FileText size={18} />
                          </span>
                          <span className={styles.docMeta}>
                            <a
                              className={styles.docName}
                              href={section.documentUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {section.documentName || "Document.pdf"}
                            </a>
                            <span className={styles.docSize}>
                              {formatBytes(section.documentSize)}
                            </span>
                          </span>
                          <button
                            type="button"
                            className={styles.docReplace}
                            disabled={
                              uploadingKey === selectedPage._uid + "-" + index
                            }
                            onClick={() =>
                              docInputs.current[
                                selectedPage._uid + "-" + index
                              ]?.click()
                            }
                          >
                            <Upload size={14} />
                            {uploadingKey === selectedPage._uid + "-" + index
                              ? "Uploading…"
                              : "Replace"}
                          </button>
                          <button
                            type="button"
                            className={styles.docRemove}
                            title="Remove this PDF from the section"
                            onClick={() =>
                              removeSectionDocument(selectedPage._uid, index)
                            }
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={styles.docAttach}
                          disabled={
                            uploadingKey === selectedPage._uid + "-" + index
                          }
                          onClick={() =>
                            docInputs.current[
                              selectedPage._uid + "-" + index
                            ]?.click()
                          }
                        >
                          <Paperclip size={15} />
                          {uploadingKey === selectedPage._uid + "-" + index
                            ? "Uploading…"
                            : "Attach a PDF"}
                        </button>
                      )}
                      <input
                        ref={(el) => {
                          docInputs.current[selectedPage._uid + "-" + index] = el;
                        }}
                        type="file"
                        accept="application/pdf,.pdf"
                        hidden
                        onChange={(e) =>
                          handleSectionDocument(
                            selectedPage._uid,
                            index,
                            e.target.files?.[0]
                          )
                        }
                      />
                      {section.documentUrl && (
                        <input
                          className={styles.docLabelInput}
                          type="text"
                          maxLength={120}
                          value={section.documentLabel || ""}
                          onChange={(ev) =>
                            updateSection(selectedPage._uid, index, {
                              documentLabel: ev.target.value,
                            })
                          }
                          placeholder="Link text, e.g. Download als PDF bestand"
                        />
                      )}
                      <p className={styles.hint}>
                        PDF only, up to 15 MB. It appears under this section on
                        the website.
                      </p>
                    </div>

                    {/* Q&A accordion items — only relevant to an FAQ section */}
                    {section.type === "faq" && (
                      <div className={styles.field}>
                        <label>
                          Questions &amp; answers
                          <span className={styles.countPill}>
                            {(section.items || []).length}
                          </span>
                        </label>
                        {(section.items || []).length === 0 && (
                          <p className={styles.hint}>
                            No questions yet. Each one becomes a row the visitor can expand.
                          </p>
                        )}
                        {(section.items || []).map((item, itemIndex) => (
                          <div key={itemIndex} className={styles.qaRow}>
                            <div className={styles.qaHead}>
                              <span className={styles.qaNum}>{itemIndex + 1}</span>
                              <input
                                className={styles.qaQuestion}
                                value={item.q}
                                placeholder="Question, e.g. How do I book?"
                                onChange={(e) => {
                                  const value = e.target.value;
                                  updateItems(selectedPage._uid, index, (items) =>
                                    items.map((it, i) =>
                                      i === itemIndex ? { ...it, q: value } : it
                                    )
                                  );
                                }}
                              />
                              <button
                                type="button"
                                className={styles.iconBtn}
                                disabled={itemIndex === 0}
                                title="Move up"
                                onClick={() =>
                                  updateItems(selectedPage._uid, index, (items) =>
                                    moveInList(items, itemIndex, -1)
                                  )
                                }
                              >
                                <ChevronUp size={13} />
                              </button>
                              <button
                                type="button"
                                className={styles.iconBtn}
                                disabled={itemIndex === (section.items || []).length - 1}
                                title="Move down"
                                onClick={() =>
                                  updateItems(selectedPage._uid, index, (items) =>
                                    moveInList(items, itemIndex, 1)
                                  )
                                }
                              >
                                <ChevronDown size={13} />
                              </button>
                              <button
                                type="button"
                                className={styles.iconDangerBtn}
                                title="Remove question"
                                onClick={() =>
                                  updateItems(selectedPage._uid, index, (items) =>
                                    items.filter((_, i) => i !== itemIndex)
                                  )
                                }
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <textarea
                              className={styles.qaAnswer}
                              rows={4}
                              value={item.a}
                              placeholder="Answer…"
                              onChange={(e) => {
                                const value = e.target.value;
                                updateItems(selectedPage._uid, index, (items) =>
                                  items.map((it, i) =>
                                    i === itemIndex ? { ...it, a: value } : it
                                  )
                                );
                              }}
                            />
                          </div>
                        ))}
                        <button
                          type="button"
                          className={styles.smallBtn}
                          onClick={() =>
                            updateItems(selectedPage._uid, index, (items) => [
                              ...items,
                              { q: "", a: "" },
                            ])
                          }
                        >
                          <Plus size={13} /> Add question
                        </button>
                        <p className={styles.hint}>
                          Plain text. Leave a blank line in an answer to start a new paragraph.
                        </p>
                      </div>
                    )}

                    <div className={styles.field}>
                      <label>Bullets</label>
                      {(section.bullets || []).length === 0 && (
                        <p className={styles.hint}>No bullets in this section.</p>
                      )}
                      {(section.bullets || []).map((bullet, bulletIndex) => (
                        <div key={bulletIndex} className={styles.bulletRow}>
                          <input
                            value={bullet}
                            onChange={(e) => {
                              const value = e.target.value;
                              updateBullets(selectedPage._uid, index, (bullets) =>
                                bullets.map((item, i) =>
                                  i === bulletIndex ? value : item
                                )
                              );
                            }}
                            placeholder="Bullet text"
                          />
                          <button
                            type="button"
                            className={styles.iconDangerBtn}
                            onClick={() =>
                              updateBullets(selectedPage._uid, index, (bullets) =>
                                bullets.filter((_, i) => i !== bulletIndex)
                              )
                            }
                            title="Remove bullet"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className={styles.smallBtn}
                        onClick={() =>
                          updateBullets(selectedPage._uid, index, (bullets) => [
                            ...bullets,
                            "",
                          ])
                        }
                      >
                        <Plus size={13} /> Add bullet
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {actionBar}
      {confirmModal}
    </div>
  );
};

export default StaticPages;
